import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TutorialProvider } from "./contexts/TutorialContext";
import ErrorBoundary from "./components/ErrorBoundary";
import PlanGate from "./components/PlanGate";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/layout/Navbar";
import Sidebar from "./components/layout/Sidebar";
import ContentWrapper from "./components/layout/ContentWrapper";
import Footer from "./components/layout/Footer";
import { SidebarProvider } from "./contexts/SidebarContext";
import FeedbackWidget from "./components/FeedbackWidget";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import { usePageTracking } from "./hooks/usePageTracking";

// Page tracker component (must be inside BrowserRouter)
const PageTracker = () => { usePageTracking(); return null; };

// Eagerly loaded pages (critical path)
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";

import WhatsNewModal from "./components/WhatsNewModal";
import ForceChangePasswordModal from "./components/ForceChangePasswordModal";
import RequirePlan from "./components/RequirePlan";
import ExpiredPlanModal from "./components/ExpiredPlanModal";

// Lazy loaded pages (heavy/feature pages)
const DealAnalyzerPage = lazy(() => import("./pages/DealAnalyzerPage"));
const CompFinderPage = lazy(() => import("./pages/CompFinderPage"));
const RepairEstimatorPage = lazy(() => import("./pages/RepairEstimator"));
const SharedSOWPage = lazy(() => import("./pages/SharedSOWPage"));
const AIChatPage = lazy(() => import("./pages/AIChatPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const SharedDealPage = lazy(() => import("./pages/SharedDealPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SavedDealsPage = lazy(() => import("./pages/SavedDealsPage"));
const ProjectManagerPage = lazy(() => import("./pages/ProjectManagerPage"));
const DealMarketingPage = lazy(() => import("./pages/DealMarketingPage"));
const FundingProposalPage = lazy(() => import("./pages/FundingProposalPage"));
const ContractTemplatesPage = lazy(() => import("./pages/ContractTemplatesPage"));
const ContractorsPage = lazy(() => import("./pages/ContractorsPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const WholesaleHubPage = lazy(() => import("./pages/WholesaleHubPage"));
const AnalyticsDashboardPage = lazy(() => import("./pages/AnalyticsDashboardPage"));
const CRMPage = lazy(() => import("./pages/CRMPage"));
const TeamSettingsPage = lazy(() => import("./pages/TeamSettingsPage"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const DealComparisonPage = lazy(() => import("./pages/DealComparisonPage"));
const VideoTutorialsPage = lazy(() => import("./pages/VideoTutorialsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const RenovationLeaderboardPage = lazy(() => import("./pages/RenovationLeaderboardPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const ESignPage = lazy(() => import("./pages/ESignPage"));
const ESignSignPage = lazy(() => import("./pages/ESignSignPage"));
const DocumentPrepare = lazy(() => import("./pages/DocumentPrepare"));
const PitchDeckPage = lazy(() => import("./pages/PitchDeckPage"));
const DirectoryPage = lazy(() => import("./pages/DirectoryPage"));
const DirectoryRegisterPage = lazy(() => import("./pages/DirectoryRegisterPage"));
const MyListingsPage = lazy(() => import("./pages/MyListingsPage"));
const DirectoryListingPage = lazy(() => import("./pages/DirectoryListingPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const QuickAnalyzerPage = lazy(() => import("./pages/QuickAnalyzerPage"));
const DealPipelinePage = lazy(() => import("./pages/DealPipelinePage"));
const ConfidentialityPage = lazy(() => import("./pages/ConfidentialityPage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950">
    <div className="text-center">
      <Loader2 className="w-10 h-10 text-purple-600 animate-spin mx-auto mb-4" />
      <p className="text-gray-500 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  const reCaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <GoogleReCaptchaProvider
              reCaptchaKey={reCaptchaSiteKey || ''}
              scriptProps={{
                async: true,
                defer: true,
                appendTo: 'head'
              }}
            >
            <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
              <BrowserRouter>
                <ScrollToTop />
                <PageTracker />
                <TutorialProvider>
                <SidebarProvider>
                <Navbar />
                <Sidebar />
                <WhatsNewModal />
                <ForceChangePasswordModal />
                <ExpiredPlanModal />
                <Suspense fallback={<PageLoader />}>
                <ContentWrapper>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/analyzer" element={<DealAnalyzerPage />} />
                    <Route path="/quick-analyze" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/pipeline" element={<DealPipelinePage />} />
                  <Route path="/comps" element={<CompFinderPage />} />
                  <Route path="/comp-finder" element={<CompFinderPage />} />
                  <Route path="/repair-estimator" element={<RepairEstimatorPage />} />
                  <Route path="/chat" element={<PlanGate path="/chat" featureName="AI Assistant"><AIChatPage /></PlanGate>} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />
                  <Route path="/dashboard" element={<RequirePlan><DashboardPage /></RequirePlan>} />
                  <Route path="/my-deals" element={<SavedDealsPage />} />
                  <Route path="/projects" element={<ProjectManagerPage />} />
                  <Route path="/marketing" element={<PlanGate path="/marketing" featureName="Deal Marketing"><DealMarketingPage /></PlanGate>} />
                <Route path="/marketing/:dealId" element={<PlanGate path="/marketing" featureName="Deal Marketing"><DealMarketingPage /></PlanGate>} />
                <Route path="/funding" element={<PlanGate path="/funding" featureName="Lender Funding Proposals"><FundingProposalPage /></PlanGate>} />
                <Route path="/contracts" element={<PlanGate path="/contracts" featureName="Contract Templates"><ContractTemplatesPage /></PlanGate>} />
                <Route path="/contractors" element={<PlanGate path="/contractors" featureName="Contractor Database"><ContractorsPage /></PlanGate>} />
                <Route path="/wholesale" element={<PlanGate path="/wholesale" featureName="Wholesale Hub"><WholesaleHubPage /></PlanGate>} />
                <Route path="/analytics" element={<PlanGate path="/analytics" featureName="Analytics Dashboard"><AnalyticsDashboardPage /></PlanGate>} />
                <Route path="/crm" element={<PlanGate path="/crm" featureName="CRM Lead Pipeline"><CRMPage /></PlanGate>} />
                <Route path="/compare" element={<PlanGate path="/compare" featureName="Deal Comparison"><DealComparisonPage /></PlanGate>} />
                <Route path="/teams" element={<PlanGate path="/teams" featureName="Team Management"><TeamSettingsPage /></PlanGate>} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/invite/:inviteCode" element={<AcceptInvitePage />} />
                <Route path="/shared/:dealId" element={<SharedDealPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin-login" element={<AdminLoginPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/tutorials" element={<VideoTutorialsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/confidentiality" element={<ConfidentialityPage />} />
              <Route path="/leaderboard" element={<PlanGate path="/leaderboard" featureName="Renovation Leaderboard"><RenovationLeaderboardPage /></PlanGate>} />
              <Route path="/referrals" element={<ReferralPage />} />
              <Route path="/shared-sow/:sowId" element={<SharedSOWPage />} />
              <Route path="/esign" element={<PlanGate path="/esign" featureName="E-Signature"><ESignPage /></PlanGate>} />
              <Route path="/esign/prepare/:docId" element={<DocumentPrepare />} />
              <Route path="/esign/sign/:token" element={<ESignSignPage />} />
              <Route path="/pitch-deck" element={<PlanGate path="/pitch-deck" featureName="Pitch Deck Generator"><PitchDeckPage /></PlanGate>} />
              <Route path="/pitch-deck/s/:token" element={<PitchDeckPage shared />} />
              <Route path="/directory" element={<DirectoryPage />} />
              <Route path="/directory/register" element={<DirectoryRegisterPage />} />
              <Route path="/directory/my-listings" element={<MyListingsPage />} />
              <Route path="/directory/listing/:listingId" element={<DirectoryListingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
            </Routes>
            </ContentWrapper>
            </Suspense>
            <Footer />
            <FeedbackWidget />
            <Toaster position="top-center" richColors closeButton />
            </SidebarProvider>
            </TutorialProvider>
          </BrowserRouter>
        </div>
        </GoogleReCaptchaProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  </HelmetProvider>
  );
}

export default App;
